"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { signOut } from "next-auth/react";
import {
  Logo,
  ScrollArea,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  useSidebar,
} from "visuais";
import { UserMenuContent, sidebarNavigation, SiteHeader } from "visuais";

interface TesteSidebarProps {
  children: React.ReactNode;
}

// Componente interno que gerencia o hover
function SidebarComHover({ children }: { children: React.ReactNode }) {
  const sidebar = useSidebar();
  const setOpen = sidebar?.setOpen;

  return (
    <Sidebar 
      variant="sidebar" 
      collapsible="icon"
      className="relative transition-all duration-300"
      onMouseEnter={() => setOpen?.(true)}
      onMouseLeave={() => setOpen?.(false)}
    >
      {children}
    </Sidebar>
  );
}

export function SidebarLayout({ children }: TesteSidebarProps) {
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const [env, setEnv] = useState<string | null>(null);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setEnv(process.env.NEXT_PUBLIC_ENV || null);
  }, []);

  const isTest = env === "test";

  const toggleMenu = (menuKey: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const clearBrowserCaches = async () => {
    if (typeof window === "undefined") return;

    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    }

    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }

    if ("indexedDB" in window) {
      type IndexedDbWithDatabases = IDBFactory & {
        databases?: () => Promise<Array<{ name?: string }>>;
      };
      const indexedDb = indexedDB as IndexedDbWithDatabases;
      if (indexedDb.databases) {
        const databases = await indexedDb.databases();
        for (const db of databases) {
          if (db.name) indexedDB.deleteDatabase(db.name);
        }
      }
    }
  };

  const handleSidebarAction = async (actionKey: "clearCache") => {
    if (actionKey !== "clearCache") return;

    const loadingToast = toast.loading("Limpando cache (modo hard)...");

    try {
      queryClient.clear();

      if (typeof window !== "undefined") {
        localStorage.clear();
        sessionStorage.clear();
      }
      await clearBrowserCaches();

      await signOut({ redirect: false });
      toast.success("Cache limpo com sucesso. Recarregando...", { id: loadingToast });
      window.location.assign(`/login?clear=${Date.now()}`);
    } catch (error) {
      console.error("Erro ao limpar cache:", error);
      toast.error("Nao foi possivel limpar totalmente o cache. Recarregando...", {
        id: loadingToast,
      });
      window.location.assign(`/login?clear=${Date.now()}`);
    }
  };
  return (
    <div className="flex h-full w-full">
      <SidebarComHover>
          <SidebarHeader>
            <Link href="/" className="flex items-center gap-2 group">
                <Logo
                  className={`transition-all duration-300 group-hover:text-primary group-data-[collapsible=icon]:size-6 size-8 ${
                    isTest ? "text-destructive" : ""
                  }`}
                  color={isTest ? "var(--destructive)" : "var(--primary)"}
                />
                <span className={`text-base font-medium transition-all duration-300 group-data-[collapsible=icon]:hidden ${
                  isTest ? "group-hover:text-destructive" : "group-hover:text-primary"
                }`}>
                  RodoAPP{isTest ? " - DEV" : ""}
                </span>
              </Link>
          </SidebarHeader>

          <SidebarContent>
            <ScrollArea
              className="h-[calc(100vh-10rem)]"
            >
            {Object.entries(sidebarNavigation).map(([groupKey, items]) => (
              <SidebarGroup key={groupKey}>
                <SidebarGroupLabel>
                  {groupKey.charAt(0).toUpperCase() + groupKey.slice(1)}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {items.map((item, index) => {
                      // Verifica se é NavItem (tem href) ou NavGroup (tem subItems)
                      if ('href' in item) {
                        const Icon = item.icon;
                        const isActive = pathname === item.href;
                        
                        return (
                          <SidebarMenuItem key={index}>
                            <SidebarMenuButton
                              asChild
                              isActive={isActive}
                              tooltip={item.label}
                            >
                              <Link href={item.href}>
                                <Icon className="h-4 w-4" />
                                <span>{item.label}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      } else if ("actionKey" in item) {
                        const Icon = item.icon;
                        const isDanger = item.variant === "danger";

                        return (
                          <SidebarMenuItem key={index}>
                            <SidebarMenuButton
                              tooltip={item.label}
                              onClick={() => void handleSidebarAction(item.actionKey)}
                              className={isDanger ? "text-destructive hover:text-destructive" : "text-foreground hover:text-primary cursor-pointer"}
                            >
                              <Icon className="h-4 w-4" />
                              <span>{item.label}</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        );
                      } else if ('subItems' in item) {
                        // Item com submenu
                        const Icon = item.icon;
                        const hasActiveSubItem = item.subItems.some(subItem => pathname === subItem.href);
                        const menuKey = `${groupKey}-${index}`;
                        const isOpen = openMenus[menuKey] || false;
                        
                        return (
                          <SidebarMenuItem key={index}>
                            <SidebarMenuButton
                              tooltip={item.label}
                              isActive={hasActiveSubItem}
                              onClick={() => toggleMenu(menuKey)}
                            >
                              <Icon className="h-4 w-4" />
                              <span>{item.label}</span>
                              <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                            </SidebarMenuButton>
                            {isOpen && (
                              <SidebarMenuSub>
                                {item.subItems.map((subItem, subIndex) => {
                                  const SubIcon = subItem.icon;
                                  const isSubActive = pathname === subItem.href;
                                  
                                  return (
                                    <SidebarMenuSubItem key={subIndex}>
                                      <SidebarMenuSubButton
                                        asChild
                                        isActive={isSubActive}
                                      >
                                        <Link href={subItem.href}>
                                          <SubIcon className="h-4 w-4" />
                                          <span>{subItem.label}</span>
                                        </Link>
                                      </SidebarMenuSubButton>
                                    </SidebarMenuSubItem>
                                  );
                                })}
                              </SidebarMenuSub>
                            )}
                          </SidebarMenuItem>
                        );
                      }
                      return null;
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
            </ScrollArea>
          </SidebarContent>

          <SidebarFooter>
            <div className="sticky bottom-0 z-[200] isolation-isolate bg-background dark:bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 rounded-t-xl shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
              <UserMenuContent />
            </div>
          </SidebarFooter>
        </SidebarComHover>

        <div className="flex flex-col flex-1">
          <div className="border-b shadow h-12">
            <SiteHeader />
          </div>
          <main className="flex-1 overflow-hidden max-h-[calc(100vh-3rem)]">
            <ScrollArea className="h-full">
              {children}
            </ScrollArea>
          </main>
        </div>
      </div>
    );
  }
